import { useEffect, useState } from 'react';
import { Column, Tab, TabPanel, Tabs, Typography } from '@tempmee/react';
import { TabBadge } from '../../TabBadge';
import { PendingReviewShiftsTab, TodayShiftsTab } from './components';
import { ActiveShiftTabEnum } from './enums';
import { useActiveShifts } from './hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnforceOfficeDashboardV2, BestInDentalV2 } from '~/constants/ff.posthog';
import { usePendingBidFeedback } from '~/domain/BestInDental';
import { BidPendingFeedbackTab } from '~/components/BestInDental/BidPendingFeedbackTab';
import {
  fireDashboardTodayShiftTabClicked,
  fireDashboardPendingReviewTabClicked,
} from '~/posthogs/events';

export const ActiveShift = () => {
  const isBidV2 = useFeatureFlag(BestInDentalV2);
  // `enabled: isBidV2` — when the flag is off the query never fires, so
  // there is zero network/behavioral change from this tab's addition.
  // totalCount is the server-designated badge count (awards-service#37) and
  // reflects the same filters as the rows; it is 0 when no cycle is open.
  const { data: pendingBidFeedback } = usePendingBidFeedback(isBidV2);
  const bidFeedbackCount = isBidV2 ? pendingBidFeedback?.totalCount ?? 0 : 0;

  const { activeTab: defaultActiveTab, counters } = useActiveShifts(bidFeedbackCount);

  const [activeTab, setActiveTab] = useState<ActiveShiftTabEnum>(defaultActiveTab);

  const useOfficeDashboardV2 = useFeatureFlag(EnforceOfficeDashboardV2);

  useEffect(() => {
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab]);

  if (counters.todays === 0 && counters.reviews === 0 && counters.bidFeedback === 0) {
    return null;
  }

  return (
    <Column
      className="dashboard-active-shifts"
      py={6}
      px={12}
      data-cy="active-shifts-container"
    >
      {!useOfficeDashboardV2 && (
        <Typography
          as="span"
          bold
          size="heading_3"
          textAlign="center"
          data-cy="active-shifts-title"
          style={{ marginInline: 'auto' }}
        >
          Active Shifts
        </Typography>
      )}

      <Tabs
        defaultValue={ActiveShiftTabEnum.Today}
        value={activeTab}
        onChange={(tab) => {
          const typedTab = tab as ActiveShiftTabEnum;
          if (typedTab === ActiveShiftTabEnum.Today) fireDashboardTodayShiftTabClicked();
          if (typedTab === ActiveShiftTabEnum.PendingReview) fireDashboardPendingReviewTabClicked();
          setActiveTab(typedTab);
        }}
        style={{ alignItems: 'start', gap: 16, marginTop: 24, width: '100%' }}
        data-cy="active-shifts-tabs"
      >
        <Tab
          value={ActiveShiftTabEnum.Today}
          data-cy="todays-shifts-tab"
          label={
            <>
              Today&apos;s Professionals <TabBadge count={counters.todays || undefined} />
            </>
          }
        />
        <Tab
          value={ActiveShiftTabEnum.PendingReview}
          data-cy="pending-review-tab"
          label={
            <>
              Pending Review <TabBadge count={counters.reviews || undefined} activeColor="error" />
            </>
          }
        />
        {isBidV2 && counters.bidFeedback > 0 && (
          <Tab
            value={ActiveShiftTabEnum.BidFeedback}
            data-cy="bid-feedback-tab"
            label={
              <>
                Feedback Needed <TabBadge count={counters.bidFeedback} activeColor="error" />
              </>
            }
          />
        )}
        <TabPanel
          value={ActiveShiftTabEnum.Today}
          style={{ width: '100%' }}
          data-cy="active-shifts-tab-panel"
        >
          <TodayShiftsTab />
        </TabPanel>
        <TabPanel value={ActiveShiftTabEnum.PendingReview} style={{ width: '100%' }}>
          <PendingReviewShiftsTab />
        </TabPanel>
        {isBidV2 && counters.bidFeedback > 0 && (
          <TabPanel value={ActiveShiftTabEnum.BidFeedback} className="w-full">
            <BidPendingFeedbackTab />
          </TabPanel>
        )}
      </Tabs>
    </Column>
  );
};
